/**
 * Native external-open bridge.
 *
 * Chrome can dispatch intent:// links itself. The Capacitor Android
 * WebView cannot be trusted to do that, and Android WebView has no
 * built-in PDF renderer. Document chips in the native app therefore
 * call this bridge from the user's tap and let Android resolve the
 * signed attachment URL with the system viewer/download stack.
 */

import { Capacitor, registerPlugin } from '@capacitor/core'

export interface AgentChatExternalOpenPlugin {
  openUrl(options: {
    url: string
    mimeType?: string | null
    filename?: string | null
    fallbackUrl?: string | null
    packageName?: string | null
    chooserTitle?: string | null
    forceChooser?: boolean | null
  }): Promise<{ ok: boolean }>
}

const AgentChatExternalOpen = registerPlugin<AgentChatExternalOpenPlugin>(
  'AgentChatExternalOpen',
  {
    web: () => Promise.resolve({
      openUrl: async ({ url }: { url: string }) => {
        const opened = window.open(url, '_blank', 'noopener,noreferrer')
        if (!opened) window.location.assign(url)
        return { ok: true }
      },
    }),
  },
)

export function isAgentChatNativeApp(): boolean {
  const ua =
    typeof navigator !== 'undefined' ? navigator.userAgent : ''
  return Capacitor.isNativePlatform() || /AgentChatNative\/\d+/i.test(ua)
}

export function isNativeExternalOpenAvailable(): boolean {
  return (
    isAgentChatNativeApp() &&
    Capacitor.isPluginAvailable('AgentChatExternalOpen')
  )
}

export async function openNativeExternalUrl(options: {
  url: string
  mimeType?: string | null
  filename?: string | null
  fallbackUrl?: string | null
  packageName?: string | null
  chooserTitle?: string | null
  forceChooser?: boolean | null
}): Promise<void> {
  await AgentChatExternalOpen.openUrl(options)
}
